import Fuse, { FuseResult } from 'fuse.js';
import { sortByDate } from 'src/components/Table/sorting';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';

interface IBaseServiceRequest extends IServiceRequest {
  tarionItemNumber?: string | null;
}

export interface IFilterServiceRequestsOptions {
  searchString: string;
  subscribedOnly?: boolean;
}

interface IProcessedServiceRequest extends IBaseServiceRequest {
  name: string;
  isGroup: boolean;
}

interface IServiceRequestChild extends IProcessedServiceRequest {
  isGroup: false;
  groupId: string;
  groupName: string;
}

interface IServiceRequestGroup extends IProcessedServiceRequest {
  isGroup: true;
  children: IServiceRequestChild[];
  groupId: string;
  groupName: string;
}

interface IServiceRequestStandalone extends IProcessedServiceRequest {
  isGroup: false;
  groupId: string | null;
  groupName: string | null;
  children?: never;
}

const hasTarionNumbers = (serviceRequests: IBaseServiceRequest[]): boolean => {
  return serviceRequests.some(
    (request: IBaseServiceRequest) => request.tarionItemNumber !== null
  );
};

const normalizeTarionItemNumber = (
  tarionItemNumber: string | null,
  shouldNormalize: boolean
): string | null => {
  if (!shouldNormalize) return tarionItemNumber;
  return tarionItemNumber || 'N/A';
};

export const searchServiceRequestsGeneralUtil = (
  serviceRequests: IServiceRequest[],
  searchString: string
): IServiceRequest[] => {
  const fuse = new Fuse(serviceRequests, {
    minMatchCharLength: 2,
    threshold: 0.3,
    keys: [
      'number',
      'description',
      'area',
      'item',
      'type',
      'status',
      'ticketNumber',
    ],
  });

  if (searchString.length < 3) {
    return serviceRequests;
  }

  return fuse
    .search(searchString)
    .map(
      (result: FuseResult<IServiceRequest>) => result.item as IServiceRequest
    );
};

const sortGroupChildrenByTarionNumber = (
  groups: IServiceRequestGroup[]
): IServiceRequestGroup[] => {
  const sortedGroup: IServiceRequestGroup[] = groups.map(
    (group: IServiceRequestGroup) => {
      if (group.children && group.children.length > 1) {
        const unsortedChildren: IServiceRequestChild[] = group.children;

        const sortedChildren: IServiceRequestChild[] = [
          ...unsortedChildren,
        ].sort((a: IServiceRequestChild, b: IServiceRequestChild) => {
          const aTarionNumber: string = a.tarionItemNumber || '';
          const bTarionNumber: string = b.tarionItemNumber || '';

          return aTarionNumber.localeCompare(bTarionNumber, undefined, {
            numeric: true,
            sensitivity: 'base',
          });
        });

        return {
          ...group,
          children: sortedChildren,
        };
      }

      return group;
    }
  );

  return sortedGroup;
};

const buildGroupChildSR = (
  serviceRequest: IBaseServiceRequest,
  shouldNormalizeTarion: boolean
): IServiceRequestChild => {
  const child: IServiceRequestChild = {
    ...serviceRequest,
    tarionItemNumber: normalizeTarionItemNumber(
      serviceRequest.tarionItemNumber,
      shouldNormalizeTarion
    ),
    name: serviceRequest.number,
    isGroup: false,
  } as IServiceRequestChild;

  return child;
};

const buildStandaloneSR = (
  sr: IBaseServiceRequest,
  shouldNormalizeTarion: boolean
): IServiceRequestStandalone => {
  const standalone: IServiceRequestStandalone = {
    ...sr,
    tarionItemNumber: normalizeTarionItemNumber(
      sr.tarionItemNumber,
      shouldNormalizeTarion
    ),
    name: sr.number,
    isGroup: false,
  };

  return standalone;
};

const buildGroupFromFirstChild = (
  sr: IBaseServiceRequest,
  shouldNormalizeTarion: boolean
): IServiceRequestGroup => {
  const firstChild: IServiceRequestChild = buildGroupChildSR(
    sr,
    shouldNormalizeTarion
  );

  /* Groups themselves don't carry a tarion number — only their children do.
   * Force null after the spread so the group row's tarion cell renders empty. */
  const newGroup: IServiceRequestGroup = {
    ...sr,
    id: sr.groupId,
    name: sr.groupName,
    children: [firstChild],
    isGroup: true,
    tarionItemNumber: null,
  } as IServiceRequestGroup;

  return newGroup;
};

// Reshapes the flat dashboard endpoint response into table-ready rows:
// reduces SRs that share a groupId into a parent { isGroup: true, children: [...] }
// row, and aliases each SR's `number` field as `name` to satisfy the legacy
// table column contract (helpers.tsx renders `${data.name} | ${data.stratalot}`).
export const nestServiceRequestsForTable = (
  serviceRequests: IServiceRequest[]
): (IServiceRequestGroup | IServiceRequestStandalone)[] => {
  const shouldNormalizeTarion: boolean = hasTarionNumbers(serviceRequests);

  const standalones: IServiceRequestStandalone[] = [];
  const groups: IServiceRequestGroup[] = [];

  for (const sr of serviceRequests as IBaseServiceRequest[]) {
    if (!sr.groupId) {
      const standalone: IServiceRequestStandalone = buildStandaloneSR(
        sr,
        shouldNormalizeTarion
      );

      standalones.push(standalone);
      continue;
    }

    const existingGroup: IServiceRequestGroup | undefined = groups.find(
      (g: IServiceRequestGroup) => g.id === sr.groupId
    );

    if (existingGroup) {
      const child: IServiceRequestChild = buildGroupChildSR(
        sr,
        shouldNormalizeTarion
      );

      existingGroup.children.push(child);
      continue;
    }

    const newGroup: IServiceRequestGroup = buildGroupFromFirstChild(
      sr,
      shouldNormalizeTarion
    );

    groups.push(newGroup);
  }

  const sortedGroups: IServiceRequestGroup[] =
    sortGroupChildrenByTarionNumber(groups);

  const orderedRows: (IServiceRequestStandalone | IServiceRequestGroup)[] = [
    ...standalones,
    ...sortedGroups,
  ];

  return orderedRows;
};

export const filterServiceRequests = (
  serviceRequests: IServiceRequest[],
  { searchString, subscribedOnly }: IFilterServiceRequestsOptions
): IServiceRequest[] => {
  if (Array.isArray(serviceRequests) === false) {
    return [];
  }

  let filteredServiceRequests: IServiceRequest[] = serviceRequests;

  if (subscribedOnly) {
    filteredServiceRequests = filteredServiceRequests.filter(
      (sr: IServiceRequest) => sr.isSubscribed === true
    );
  }

  const isSearching: boolean = searchString.length > 2;

  if (isSearching) {
    filteredServiceRequests = searchServiceRequestsGeneralUtil(
      filteredServiceRequests,
      searchString
    ) as IServiceRequest[];
  }

  filteredServiceRequests = nestServiceRequestsForTable(filteredServiceRequests);

  filteredServiceRequests.sort((a: any, b: any) =>
    sortByDate(b.createdDate, a.createdDate)
  );

  return filteredServiceRequests;
};
